import React from "react";
import "./textStyleControls.css";

export const FONT_FAMILY_OPTIONS = [
  { value: "", label: "Default" },
  { value: "Georgia, 'Times New Roman', serif", label: "Georgia" },
  { value: "'Trebuchet MS', 'Segoe UI', sans-serif", label: "Trebuchet" },
  { value: "'Courier New', Courier, monospace", label: "Courier" },
  { value: "'Poppins', sans-serif", label: "Poppins", google: "Poppins:wght@400;600;700" },
  {
    value: "'Playfair Display', Georgia, serif",
    label: "Playfair Display",
    google: "Playfair+Display:wght@400;600;700",
  },
  {
    value: "'Merriweather', Georgia, serif",
    label: "Merriweather",
    google: "Merriweather:wght@400;700",
  },
  {
    value: "'Source Sans 3', 'Segoe UI', sans-serif",
    label: "Source Sans 3",
    google: "Source+Sans+3:wght@400;600;700",
  },
];

const loadedGoogle = new Set();

export function ensureGoogleFont(familyValue) {
  const opt = FONT_FAMILY_OPTIONS.find((o) => o.value === familyValue);
  if (!opt?.google || loadedGoogle.has(opt.google)) return;
  if (typeof document === "undefined") return;
  const id = `wgt-gf-${opt.google.replace(/[^a-z0-9]+/gi, "-")}`;
  if (document.getElementById(id)) {
    loadedGoogle.add(opt.google);
    return;
  }
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${opt.google}&display=swap`;
  document.head.appendChild(link);
  loadedGoogle.add(opt.google);
}

const SIZE_DRAFT_RE = /^\d*$/;

/**
 * Typography toolbar for a title or rules block.
 * Expects style: { font_family, font_size_desktop, font_size_mobile, color }
 */
export default function TextStyleControls({
  label = "Typography",
  style = {},
  onChange,
  previewText = "",
  onSizeBlur,
}) {
  const fontFamily = style.font_family || "";
  const fontSizeDesktop = style.font_size_desktop ?? 16;
  const fontSizeMobile = style.font_size_mobile ?? 14;
  const textColor = style.color || "#333333";

  const patch = (key, value) => {
    if (key === "font_family") ensureGoogleFont(value);
    onChange?.({ ...style, [key]: value });
  };

  const patchSizeDraft = (key, raw) => {
    const v = String(raw ?? "");
    if (v === "" || SIZE_DRAFT_RE.test(v)) {
      patch(key, v === "" ? "" : v);
    }
  };

  const previewPx = (() => {
    const n = Number.parseInt(String(fontSizeDesktop), 10);
    if (!Number.isFinite(n) || n <= 0) return 16;
    return Math.min(n, 28);
  })();

  return (
    <div className="tsc-panel" aria-label={label}>
      <div className="tsc-body">
        <label className="tsc-field tsc-field--family">
          <span>Font family</span>
          <select value={fontFamily} onChange={(e) => patch("font_family", e.target.value)}>
            {FONT_FAMILY_OPTIONS.map((o) => (
              <option key={o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        {previewText ? (
          <div className="tsc-preview-block">
            <span className="tsc-preview-label">Preview</span>
            <div
              className="tsc-preview"
              style={{
                fontFamily: fontFamily || undefined,
                fontSize: `${previewPx}px`,
                color: textColor,
              }}
              title={previewText}
            >
              {previewText}
            </div>
          </div>
        ) : null}

        <div className="tsc-sizes">
          <label className="tsc-field tsc-field--num">
            <span>Desktop size</span>
            <div className="tsc-input-wrap">
              <input
                type="text"
                inputMode="numeric"
                value={
                  fontSizeDesktop === "" || fontSizeDesktop == null
                    ? ""
                    : String(fontSizeDesktop)
                }
                onChange={(e) => patchSizeDraft("font_size_desktop", e.target.value)}
                onBlur={() => onSizeBlur?.("font_size_desktop", style)}
                placeholder="16"
                aria-label={`${label} desktop font size`}
              />
              <em>px</em>
            </div>
          </label>

          <label className="tsc-field tsc-field--num">
            <span>Mobile size</span>
            <div className="tsc-input-wrap">
              <input
                type="text"
                inputMode="numeric"
                value={
                  fontSizeMobile === "" || fontSizeMobile == null
                    ? ""
                    : String(fontSizeMobile)
                }
                onChange={(e) => patchSizeDraft("font_size_mobile", e.target.value)}
                onBlur={() => onSizeBlur?.("font_size_mobile", style)}
                placeholder="14"
                aria-label={`${label} mobile font size`}
              />
              <em>px</em>
            </div>
          </label>

          <label className="tsc-field tsc-field--color">
            <span>Color</span>
            <div className="tsc-color-wrap">
              <input
                type="color"
                value={textColor}
                onChange={(e) => patch("color", e.target.value)}
                aria-label={`${label} color`}
                title={previewText || textColor}
              />
            </div>
          </label>
        </div>

        <p className="tsc-hint">
          Sizes in px (1–200). Clear and retype freely; blank restores default on blur.
        </p>
      </div>
    </div>
  );
}
